import React, { useState, useEffect, Fragment } from "react";
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Chip,
  LinearProgress,
  Typography,
  Grid,
  Stack,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Skeleton
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { FirebaseAuth } from "../../../../components/FirebaseAuth/firebase";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from '@mui/icons-material/Download';
import PunchClockIcon from '@mui/icons-material/PunchClock';
import SummarizeIcon from '@mui/icons-material/Summarize';
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import weekday from "dayjs/plugin/weekday";

dayjs.extend(weekday);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const Timesheets = () => {
  const theme = useTheme();
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [daysInMonth, setDaysInMonth] = useState([]);
  const [timesheetStatus, setTimesheetStatus] = useState('open');
  const [timesheetEntries, setTimesheetEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDate, setDialogDate] = useState(null);
  const [dialogEntries, setDialogEntries] = useState([]);

  const user  = FirebaseAuth.auth().currentUser; // assume this gives user.uid
  const statusChipColor = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "open":
        return "warning";
      case "approved":
        return "info";
      case "paid":
        return "success";
      default:
        return "default";
    }
  };

  useEffect(() => {
    const start = selectedDate.startOf("month");
    const end = selectedDate.endOf("month");
    const days = [];

    let current = start;
    while (current.isSameOrBefore(end)) {
      days.push(current);
      current = current.add(1, "day");
    }
    setDaysInMonth(days);
  }, [selectedDate]);

  useEffect(() => {
    if (!user) return;

    const fetchTimesheet = async () => {
      setLoading(true);
      const timesheetId = `${selectedDate.format("YYYY-MM")}`;
      const snap = await FirebaseAuth.firestore().collection('users').doc(user.uid).collection('timesheets').doc(timesheetId).get();

      if (snap.exists) {
        setTimesheetStatus(snap.data().status);
        setTimesheetEntries(snap.data().entries || []);
      } else {
        setTimesheetStatus('open');
        setTimesheetEntries([]);
      }
      setLoading(false);
    };

    fetchTimesheet();
  }, [selectedDate, user]);

  useEffect(() => {
    if (!user?.uid) return undefined;

    const timesheetId = `${selectedDate.format("YYYY-MM")}`;
    const unsubscribe = FirebaseAuth
      .firestore()
      .collection('users')
      .doc(user.uid)
      .collection('timesheets')
      .doc(timesheetId)
      .onSnapshot((snapshot) => {
        if (snapshot.exists) {
          setTimesheetStatus(snapshot.data().status || 'open');
          setTimesheetEntries(snapshot.data().entries || []);
        } else {
          setTimesheetStatus('open');
          setTimesheetEntries([]);
        }
      });

    return () => unsubscribe();
  }, [selectedDate, user?.uid]);

  const getEntriesForDate = (day) =>
    timesheetEntries.filter(
      (entry) =>
        // dayjs(entry.date).isSame(day, "day") && entry.status === "approved"
        dayjs(entry.date).isSame(day, "day")
    );

  const getTotalHoursForDate = (day) => {
    const entries = getEntriesForDate(day);
    return entries.reduce((sum, entry) => sum + (entry.hours || 0), 0);
  };

  const getAmountForEntry = (entry) => ((Number(entry.rate || 0) * Number(entry.hours || 0)) / 100);

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text(`${FirebaseAuth.auth().currentUser.displayName} - Timesheet - ${selectedDate.format("MMMM YYYY")}`, 14, 15);
  
    const filtered = timesheetEntries.filter((e) =>
      dayjs(e.date?.toDate ? e.date.toDate() : e.date).isSame(selectedDate, "month")
    );
  
    autoTable(doc, {
      startY: 20,
      head: [["Date", "Project", "Hours", "Rate", "Amount", "Description", "Status"]],
      body: filtered.map((e) => [
        dayjs(e.date?.toDate ? e.date.toDate() : e.date).format("YYYY-MM-DD"),
        e.account_name || "",
        e.hours || 0,
        `$${(e.rate / 100).toFixed(2)}`,
        `$${((e.rate * e.hours) / 100).toFixed(2)}`,
        e.description || "",
        e.status || "",
      ]),
    });
  
    doc.save(`Timesheet-${selectedDate.format("YYYY-MM")}.pdf`);
  };

  const handleTileClick = (day) => {
    const entries = getEntriesForDate(day);
    if (entries.length === 0) return;
    setDialogDate(day);
    setDialogEntries(entries);
    setDialogOpen(true);
  };

  return (
    <Paper
        variant="outlined"
        sx={{ p: 0, mb:2, display: 'block', borderRadius: '0px', borderWidth: '0px', backgroundColor: theme.palette.background.paper }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box p={0} style={{ textAlign: 'center', width: '100%'}}>
            <List component="nav" sx={{p: 0}}>
                <ListItem sx={{p: 2, borderRadius: '8px'}} secondaryAction={
                    <DatePicker
                    views={["year", "month"]}
                    label="Choose Month"
                    value={selectedDate}
                    onChange={(newDate) => setSelectedDate(newDate)}
                    slotProps={{
                      field: { clearable: false },
                      textField: {
                          size: 'small',
                          label: '',
                          sx: { width: '180px' }
                      },
                    }}
                    renderInput={(params) => <TextField {...params} helperText={null} />}
                    />
                }>
                    <ListItemAvatar sx={{ mr: 'auto', minWidth: '48px' }}>
                        <Avatar variant="rounded" sx={{ height: '32px', width: '32px', bgcolor: theme.palette.grey[700] }}>
                            <PunchClockIcon fontSize="small" />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                    primary={
                        <Fragment>
                            <Typography
                                component="span"
                                sx={{ color: 'text.primary', display: 'inline', fontWeight: 500, fontSize: '16px' }}
                            >
                              Timesheets
                            </Typography>
                        </Fragment>
                    }/>
                </ListItem>
            </List>
        </Box>
        <Box p={0} style={{ textAlign: 'center', width: '100%'}}>
            {loading ? (
                <Box sx={{ display: "block", justifyContent: "center", p: 2 }}>
                    <Skeleton variant="text" width={'100%'} sx={{ fontSize: '1rem' }} />
                    <Skeleton variant="rounded" width={'100%'} height={80} sx={{ mb: 1}} />
                    <Skeleton variant="rounded" width={'100%'} height={80} sx={{ mb: 1}} />
                    <Skeleton variant="rounded" width={'100%'} height={80} sx={{ mb: 1}} />
                    <Skeleton variant="rounded" width={'100%'} height={80} sx={{ mb: 1}} />
                </Box>
            ) : (
            <>
                <Box sx={{ p: 2 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      overflow: "hidden",
                      borderRadius: 2,
                      borderColor: "divider",
                      backgroundColor: "background.paper",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.action.hover,
                      }}
                    >
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((dayName) => (
                        <Box key={dayName} sx={{ py: 1, textAlign: "center" }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
                            {dayName}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                        gap: 1,
                        p: 1,
                      }}
                    >
                      {Array(daysInMonth[0]?.day() || 0).fill(null).map((_, i) => (
                        <Box
                          key={`empty-${i}`}
                          sx={{
                            minHeight: 96,
                            borderRadius: 1.5,
                            backgroundColor: theme.palette.action.hover,
                            opacity: 0.45,
                          }}
                        />
                      ))}

                      {daysInMonth.map((day) => {
                        const totalHours = getTotalHoursForDate(day);
                        const entriesCount = getEntriesForDate(day).length;
                        const hasEntries = totalHours > 0;
                        return (
                          <Paper
                            key={day.format()}
                            onClick={() => handleTileClick(day)}
                            variant="outlined"
                            sx={{
                              p: 1.25,
                              minHeight: 96,
                              borderRadius: 1.5,
                              borderColor: hasEntries ? theme.palette.primary.light : theme.palette.divider,
                              backgroundColor: hasEntries ? theme.palette.primary.light + "22" : "background.paper",
                              cursor: hasEntries ? "pointer" : "default",
                              transition: "all 0.2s ease",
                              "&:hover": hasEntries
                                ? {
                                    boxShadow: 2,
                                    transform: "translateY(-1px)",
                                  }
                                : undefined,
                            }}
                          >
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {day.format("DD")}
                              </Typography>
                            </Stack>
                            <Divider sx={{ mb: 1 }} />
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: hasEntries ? "primary.main" : "text.disabled",
                                lineHeight: 1.2,
                              }}
                            >
                              {hasEntries ? `${Number(totalHours).toFixed(1)}h` : "0h"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {hasEntries ? `${entriesCount} ${entriesCount === 1 ? "entry" : "entries"}` : "No entries"}
                            </Typography>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Paper>
                </Box>

                {/* Monthly Summary */}
                <Box sx={{ p: 2, textAlign: "left" }}>
                  <Divider sx={{ mb: 2 }} />

                  {(() => {
                    const totalWorkedHours = daysInMonth.reduce((sum, day) => sum + getTotalHoursForDate(day), 0);
                    const workingDaysInMonth = daysInMonth.filter((d) => d.day() !== 0 && d.day() !== 6).length;
                    const totalAvailableHours = workingDaysInMonth * 8;
                    const availabilityPercent = totalAvailableHours > 0
                      ? Number(((totalWorkedHours / totalAvailableHours) * 100).toFixed(1))
                      : 0;
                    const totalAmount = timesheetEntries
                      .filter((e) => {
                        const entryDate = dayjs(e.date?.toDate ? e.date.toDate() : e.date);
                        return entryDate.isSame(selectedDate, "month");
                      })
                      .reduce(
                        (sum, e) => sum + (Number(e.hours || 0) * Number(e.rate || 0)) / 100,
                        0
                      );
                    const hasEntriesThisMonth = timesheetEntries.some((e) => {
                      const entryDate = dayjs(e.date?.toDate ? e.date.toDate() : e.date);
                      return entryDate.isSame(selectedDate, "month");
                    });

                    return (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          background: "linear-gradient(to bottom, #fafafa, #f5f5f5)",
                          borderColor: "divider",
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <SummarizeIcon fontSize="small" />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Monthly Summary
                            </Typography>
                          </Stack>
                          <Chip
                            label={(timesheetStatus || "open").toLowerCase()}
                            color={statusChipColor(timesheetStatus)}
                            size="small"
                            sx={{ textTransform: "capitalize", borderRadius: 1 }}
                          />
                        </Stack>

                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                          {selectedDate.format("MMMM YYYY")}
                        </Typography>

                        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={6}>
                            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                              <Typography variant="caption" color="text.secondary">Total hours</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {Number(totalWorkedHours).toFixed(1)}h
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                              <Typography variant="caption" color="text.secondary">Total earnings</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(totalAmount)}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>

                        <Box sx={{ mb: 1.5 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Capacity utilization
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {availabilityPercent}% ({Number(totalWorkedHours).toFixed(1)}h / {totalAvailableHours}h)
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(availabilityPercent, 100)}
                            sx={{ borderRadius: 5, height: 8 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                            Based on {workingDaysInMonth} working days x 8h = {totalAvailableHours}h monthly capacity.
                          </Typography>
                        </Box>

                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={handleExportPDF}
                          disabled={!hasEntriesThisMonth}
                          sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                          Download Timesheet (PDF)
                        </Button>
                      </Paper>
                    );
                  })()}
                </Box>
            </>
            )}

            {/* Dialog */}
            <Dialog
              open={dialogOpen}
              onClose={() => setDialogOpen(false)}
              maxWidth="sm"
              fullWidth
              PaperProps={{ sx: { borderRadius: 2.5, overflow: "hidden" } }}
            >
              <DialogTitle sx={{ p: 0, position: "relative" }}>
                <Box sx={{ p: 2.5, borderTop: "4px solid", borderColor: "primary.main" }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pr: 5 }}>
                    <Avatar variant="rounded" sx={{ width: 42, height: 42, bgcolor: "rgba(25, 118, 210, 0.08)", color: "primary.main" }}>
                      <PunchClockIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                        Work Log Details
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {dialogDate?.format("dddd, MMMM DD, YYYY")}
                      </Typography>
                    </Box>
                  </Stack>
                  <IconButton
                    size="small"
                    sx={{ position: "absolute", top: 18, right: 18, color: "text.secondary" }}
                    onClick={() => setDialogOpen(false)}
                    aria-label="Close work log details"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent dividers sx={{ p: 2.5, bgcolor: "grey.50" }}>
                <Stack spacing={1.5}>
                  {dialogEntries.map((entry, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 2, borderRadius: 1.75, bgcolor: "background.paper" }}>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                              {entry.account_name || "Work Item"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Submitted work entry
                            </Typography>
                          </Box>
                          <Chip
                            label={(entry.status || "open").toLowerCase()}
                            size="small"
                            color={statusChipColor(entry.status)}
                            sx={{ textTransform: "capitalize", borderRadius: 1, fontWeight: 700 }}
                          />
                        </Stack>
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
                          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.25, bgcolor: "grey.50" }}>
                            <Typography variant="caption" color="text.secondary">Hours</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                              {Number(entry.hours || 0).toFixed(1)}h
                            </Typography>
                          </Paper>
                          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.25, bgcolor: "grey.50" }}>
                            <Typography variant="caption" color="text.secondary">Amount</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                              {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(getAmountForEntry(entry))}
                            </Typography>
                          </Paper>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>
                            Description
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap", lineHeight: 1.65 }}>
                            {entry.description || "No description provided."}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </DialogContent>
            </Dialog>
        </Box>
        </LocalizationProvider>
    </Paper>
    
  );
};

export default Timesheets;
